import React, { useState, useMemo } from 'react'
import { Pagination } from '@material-ui/lab'
import {
    useTheme,
    TableSortLabel,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@material-ui/core'
import { GetApp } from '@material-ui/icons'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { Container } from '../../components'
import { usePagination } from '../../hooks'
import { MoveInSurveySummary } from '../../hooks/useMoveInSurvey'
import { useUserTags } from '../../contexts/UserTagsContext'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    summaryList: MoveInSurveySummary[]
    onSelectProperty: (apartmentId: number) => void
    onExport: (apartmentId: number, apartmentName: string) => void
    onAnalyze: (apartmentId: number) => void
}

const NameASC = 'NameASC'
const NameDESC = 'NameDESC'
const InspASC = 'InspASC'
const InspDESC = 'InspDESC'
const CountASC = 'CountASC'
const CountDESC = 'CountDESC'
const RespRateASC = 'RespRateASC'
const RespRateDESC = 'RespRateDESC'
const RatingASC = 'RatingASC'
const RatingDESC = 'RatingDESC'
const UnitsReadyASC = 'UnitsReadyASC'
const UnitsReadyDESC = 'UnitsReadyDESC'

const getResponseRate = (row: {
    survey_count: number
    inspection_count: number
}) => (row.inspection_count > 0 ? row.survey_count / row.inspection_count : -1)

export const ratingColor = (
    rating: number | null,
    palette: {
        success: { main: string }
        warning: { main: string }
        error: { main: string }
    },
) => {
    if (rating === null) return '#bdbdbd'
    if (rating >= 7) return palette.success.main
    if (rating >= 4) return palette.warning.main
    return palette.error.main
}

export const PortfolioTable: React.FC<Props> = ({
    summaryList,
    onSelectProperty,
    onExport,
    onAnalyze,
}) => {
    const theme = useTheme()
    const { userTags } = useUserTags()
    const [orderBy, setOrderBy] = useState('')

    const sortedList = useMemo(() => {
        const list = [...summaryList]
        switch (orderBy) {
            case NameASC:
                list.sort((a, b) =>
                    a.apartment_name.localeCompare(b.apartment_name),
                )
                break
            case NameDESC:
                list.sort((a, b) =>
                    b.apartment_name.localeCompare(a.apartment_name),
                )
                break
            case InspASC:
                list.sort((a, b) => a.inspection_count - b.inspection_count)
                break
            case InspDESC:
                list.sort((a, b) => b.inspection_count - a.inspection_count)
                break
            case CountASC:
                list.sort((a, b) => a.survey_count - b.survey_count)
                break
            case CountDESC:
                list.sort((a, b) => b.survey_count - a.survey_count)
                break
            case RespRateASC:
                list.sort((a, b) => getResponseRate(a) - getResponseRate(b))
                break
            case RespRateDESC:
                list.sort((a, b) => getResponseRate(b) - getResponseRate(a))
                break
            case RatingASC:
                list.sort((a, b) => {
                    if (a.average_rating === null && b.average_rating === null)
                        return 0
                    if (a.average_rating === null) return 1
                    if (b.average_rating === null) return -1
                    return a.average_rating - b.average_rating
                })
                break
            case RatingDESC:
                list.sort((a, b) => {
                    if (a.average_rating === null && b.average_rating === null)
                        return 0
                    if (a.average_rating === null) return 1
                    if (b.average_rating === null) return -1
                    return b.average_rating - a.average_rating
                })
                break
            case UnitsReadyASC:
                list.sort((a, b) => a.units_ready - b.units_ready)
                break
            case UnitsReadyDESC:
                list.sort((a, b) => b.units_ready - a.units_ready)
                break
        }
        return list
    }, [summaryList, orderBy])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        sortedList,
    )

    const toggleSort = (ascKey: string, descKey: string) => {
        setOrderBy((prev) => (prev !== ascKey ? ascKey : descKey))
    }

    const renderTags = (apartmentId: number) => {
        const relevantTags = userTags.filter((tag) =>
            tag.apartments.includes(apartmentId),
        )
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {relevantTags.map((tag) => (
                    <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{
                            backgroundColor: tag.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                        }}
                    />
                ))}
            </div>
        )
    }

    const headCellStyle: React.CSSProperties = {
        fontWeight: 600,
        whiteSpace: 'nowrap',
    }

    return (
        <Container
            direction="column"
            style={{
                marginTop: theme.spacing(2),
                padding: theme.spacing(0, 2),
            }}
        >
            <Paper variant="outlined" style={{ borderRadius: 8 }}>
                <TableContainer
                    style={{
                        maxHeight: 'calc(100vh - 280px)',
                        overflowY: 'auto',
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                        toggleSort(NameASC, NameDESC)
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === NameASC ||
                                            orderBy === NameDESC
                                        }
                                        direction={
                                            orderBy === NameASC ? 'asc' : 'desc'
                                        }
                                    >
                                        Property Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={headCellStyle}>
                                    Tags
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    align="center"
                                    onClick={() =>
                                        toggleSort(InspASC, InspDESC)
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === InspASC ||
                                            orderBy === InspDESC
                                        }
                                        direction={
                                            orderBy === InspASC ? 'asc' : 'desc'
                                        }
                                    >
                                        Move In Inspections
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    align="center"
                                    onClick={() =>
                                        toggleSort(CountASC, CountDESC)
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === CountASC ||
                                            orderBy === CountDESC
                                        }
                                        direction={
                                            orderBy === CountASC
                                                ? 'asc'
                                                : 'desc'
                                        }
                                    >
                                        Surveys
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    align="center"
                                    onClick={() =>
                                        toggleSort(RespRateASC, RespRateDESC)
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === RespRateASC ||
                                            orderBy === RespRateDESC
                                        }
                                        direction={
                                            orderBy === RespRateASC
                                                ? 'asc'
                                                : 'desc'
                                        }
                                    >
                                        Response Rate
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    align="center"
                                    onClick={() =>
                                        toggleSort(RatingASC, RatingDESC)
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === RatingASC ||
                                            orderBy === RatingDESC
                                        }
                                        direction={
                                            orderBy === RatingASC
                                                ? 'asc'
                                                : 'desc'
                                        }
                                    >
                                        Avg Rating
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{
                                        ...headCellStyle,
                                        cursor: 'pointer',
                                    }}
                                    align="center"
                                    onClick={() =>
                                        toggleSort(
                                            UnitsReadyASC,
                                            UnitsReadyDESC,
                                        )
                                    }
                                >
                                    <TableSortLabel
                                        active={
                                            orderBy === UnitsReadyASC ||
                                            orderBy === UnitsReadyDESC
                                        }
                                        direction={
                                            orderBy === UnitsReadyASC
                                                ? 'asc'
                                                : 'desc'
                                        }
                                    >
                                        Units Ready
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={headCellStyle} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageData.length > 0 ? (
                                pageData.map((row) => (
                                    <TableRow
                                        key={row.apartment_id}
                                        hover
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            onSelectProperty(row.apartment_id)
                                        }
                                    >
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color:
                                                        theme.palette.primary
                                                            .main,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {row.apartment_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {renderTags(row.apartment_id)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {row.inspection_count}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {row.survey_count}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {row.inspection_count > 0
                                                    ? `${Math.round(
                                                          (row.survey_count /
                                                              row.inspection_count) *
                                                              100,
                                                      )}%`
                                                    : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.average_rating !== null ? (
                                                <Chip
                                                    label={row.average_rating.toFixed(
                                                        1,
                                                    )}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: ratingColor(
                                                            row.average_rating,
                                                            theme.palette,
                                                        ),
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        minWidth: 48,
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    N/A
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {row.units_ready}/
                                                {row.units_serviced}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            style={{ whiteSpace: 'nowrap' }}
                                        >
                                            <Tooltip title="Analyze with Lori">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onAnalyze(
                                                            row.apartment_id,
                                                        )
                                                    }}
                                                >
                                                    <LoriIcon
                                                        width={20}
                                                        height={20}
                                                        color={
                                                            theme.palette
                                                                .primary.main
                                                        }
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Export to Excel">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onExport(
                                                            row.apartment_id,
                                                            row.apartment_name,
                                                        )
                                                    }}
                                                >
                                                    <GetApp fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography
                                            variant="body1"
                                            color="textSecondary"
                                            style={{
                                                padding: theme.spacing(4, 0),
                                            }}
                                        >
                                            No survey data found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Footer */}
                <Container
                    style={{
                        ...typographyStyle(theme.typography.subtitle2),
                        borderTop: `1px solid ${theme.palette.grey[300]}`,
                        padding: theme.spacing(1, 2),
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                    }}
                >
                    <Pagination
                        count={numPages}
                        page={page}
                        onChange={(_: unknown, newPage: number) => {
                            setPage(newPage)
                        }}
                        size="small"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {start} - {end} of {sortedList.length}
                    </Typography>
                </Container>
            </Paper>
        </Container>
    )
}
